import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from '../utils/localStorageUtils';

// A feed photo upload is applied asynchronously, so the photo has no photo id until listing-tools processes it.
// Uploading or reordering again before then targets a photo that isn't there yet, so both pause for a while after
// an upload. Building photos and each floor plan's photos pause independently. The expiry is kept in localStorage so
// a reload, which shows the listing without the still-processing photo, doesn't lift the pause early.
const FEED_PHOTO_UPLOAD_LOCK_MS = 45 * 1000;
const LOCK_CHANGE_EVENT = 'zrm:feed-photo-upload-lock-change';

export type FeedPhotoUploadScope = { type: 'property' } | { type: 'floorPlan'; floorPlanId: string };

const subscribe = (listener: () => void) => {
  window.addEventListener(LOCK_CHANGE_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(LOCK_CHANGE_EVENT, listener);
    window.removeEventListener('storage', listener);
  };
};

const notifyLockChange = () => window.dispatchEvent(new Event(LOCK_CHANGE_EVENT));

export const useFeedPhotoUploadLock = ({
  listingAlias,
  scope,
  onUnlock,
}: {
  listingAlias: string;
  scope: FeedPhotoUploadScope;
  onUnlock?: () => void;
}) => {
  const storageKey =
    scope.type === 'property'
      ? `zrm-feed-photo-upload-lock:${listingAlias}:property`
      : `zrm-feed-photo-upload-lock:${listingAlias}:${scope.floorPlanId}`;
  const expiresAtMs = useSyncExternalStore(
    subscribe,
    () => Number(getLocalStorageItem(storageKey)) || 0,
    () => 0,
  );
  const isFeedPhotoUploadLocked = expiresAtMs > Date.now();

  useEffect(() => {
    if (expiresAtMs === 0) {
      return;
    }
    const timeoutId = window.setTimeout(
      () => {
        removeLocalStorageItem(storageKey);
        notifyLockChange();
      },
      Math.max(0, expiresAtMs - Date.now()),
    );
    return () => window.clearTimeout(timeoutId);
  }, [expiresAtMs, storageKey]);

  const wasLockedRef = useRef(isFeedPhotoUploadLocked);
  useEffect(() => {
    if (wasLockedRef.current && !isFeedPhotoUploadLocked) {
      onUnlock?.();
    }
    wasLockedRef.current = isFeedPhotoUploadLocked;
  }, [isFeedPhotoUploadLocked, onUnlock]);

  const startFeedPhotoUploadLock = useCallback(() => {
    setLocalStorageItem(storageKey, String(Date.now() + FEED_PHOTO_UPLOAD_LOCK_MS));
    notifyLockChange();
  }, [storageKey]);

  return { isFeedPhotoUploadLocked, startFeedPhotoUploadLock };
};
